import { useState, useEffect } from "react";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Shield, User, Building2, Dumbbell, CheckCircle, XCircle, Clock,
  Eye, FileText, Loader2, RefreshCw, AlertCircle, AlertTriangle, ChevronRight, Award, Globe
} from "lucide-react";

const statusColors = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  incomplete: "bg-slate-100 text-slate-600",
};

const statusIcons = {
  pending: Clock,
  approved: CheckCircle,
  rejected: XCircle,
  incomplete: AlertCircle,
};

const resolveUploadUrl = (url) => {
  if (!url) return "";
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.pathname.includes("/api/uploads/")) {
      return `${window.location.origin}${parsed.pathname}`;
    }
    return url;
  } catch {
    return url;
  }
};

const isPreviewableImage = (url) => /\.(jpg|jpeg|png|webp|heic)$/i.test(url || "");

export default function KYCReview() {
  const [tab, setTab] = useState("venue_owner");
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [filter, setFilter] = useState("pending");
  const [reviewType, setReviewType] = useState("identity"); // identity, credentials

  const fetchData = async () => {
    setLoading(true);
    try {
      const [submissionsRes, statsRes] = await Promise.all([
        api.get(`/admin/kyc/all?persona=${tab}&status=${filter}`),
        api.get("/admin/kyc/stats"),
      ]);
      setSubmissions(submissionsRes.data.submissions || []);
      setStats(statsRes.data);
    } catch (err) {
      toast.error("Failed to load KYC data");
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
  }, [tab, filter]);

  // Reset review type when selecting a new user
  useEffect(() => {
    if (selectedUser) {
      // Default to credentials if identity is already approved for coaches
      if (tab === "coach" && selectedUser.identity_review_status === "approved") {
        setReviewType("credentials");
      } else {
        setReviewType("identity");
      }
    }
  }, [selectedUser, tab]);

  const handleReview = async (action) => {
    if (!selectedUser) return;
    setProcessing(true);
    try {
      await api.post("/admin/kyc/review", {
        user_id: selectedUser.user_id,
        persona: tab,
        review_type: reviewType,
        action,
        notes: reviewNotes,
      });
      toast.success(`${reviewType === "credentials" ? "Credentials" : "Identity"} ${action === "approve" ? "approved" : "rejected"} successfully`);
      setSelectedUser(null);
      setReviewNotes("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Review failed");
    }
    setProcessing(false);
  };

  const getPersonaIcon = (persona) => {
    switch (persona) {
      case "venue_owner": return Building2;
      case "player": return User;
      case "coach": return Dumbbell;
      default: return User;
    }
  };

  const PersonaIcon = getPersonaIcon(tab);
  const resolvedGovIdUrl = selectedUser ? resolveUploadUrl(selectedUser.gov_id_url) : "";
  const resolvedSelfieUrl = selectedUser ? resolveUploadUrl(selectedUser.selfie_url) : "";

  return (
    <div className="p-6 space-y-6" data-testid="kyc-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
            <Shield className="w-6 h-6 text-primary" />
            KYC Review
          </h1>
          <p className="text-muted-foreground text-sm">Review and approve identity verification submissions</p>
        </div>
        <Button variant="outline" onClick={fetchData} disabled={loading}>
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-4 gap-4">
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats.total_pending}</p>
              <p className="text-xs text-muted-foreground">Total Pending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-green-600">{stats[tab]?.approved || 0}</p>
              <p className="text-xs text-muted-foreground">Approved ({tab.replace("_", " ")})</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-red-600">{stats[tab]?.rejected || 0}</p>
              <p className="text-xs text-muted-foreground">Rejected ({tab.replace("_", " ")})</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4 text-center">
              <p className="text-2xl font-bold text-amber-600">{stats[tab]?.pending || 0}</p>
              <p className="text-xs text-muted-foreground">Pending ({tab.replace("_", " ")})</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="venue_owner" className="flex items-center gap-2" data-testid="tab-venue-owner">
            <Building2 className="w-4 h-4" />
            Venue Owners
            {stats?.venue_owner?.pending > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white text-xs">{stats.venue_owner.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="player" className="flex items-center gap-2" data-testid="tab-player">
            <User className="w-4 h-4" />
            Players
            {stats?.player?.pending > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white text-xs">{stats.player.pending}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="coach" className="flex items-center gap-2" data-testid="tab-coach">
            <Dumbbell className="w-4 h-4" />
            Coaches
            {stats?.coach?.pending > 0 && (
              <Badge className="ml-1 bg-amber-500 text-white text-xs">{stats.coach.pending}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        <div className="mt-4">
          {/* Filter Buttons */}
          <div className="flex gap-2 mb-4">
            {["pending", "approved", "rejected", "all"].map((f) => (
              <Button
                key={f}
                variant={filter === f ? "default" : "outline"}
                size="sm"
                onClick={() => setFilter(f)}
                className="capitalize"
              >
                {f}
              </Button>
            ))}
          </div>

          {/* Content */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Submissions List */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <PersonaIcon className="w-5 h-5" />
                  Submissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-6 h-6 animate-spin text-primary" />
                  </div>
                ) : submissions.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Shield className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p>No {filter} submissions</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-[500px] overflow-y-auto">
                    {submissions.map((sub) => {
                      // For coaches, show the most relevant pending status
                      const effectiveStatus = tab === "coach" && sub.identity_review_status === "approved"
                        ? (sub.credentials_review_status || "incomplete")
                        : (sub.identity_review_status || "incomplete");
                      const StatusIcon = statusIcons[effectiveStatus] || Clock;
                      const isSelected = selectedUser?.user_id === sub.user_id;
                      const reviewStage = tab === "coach" && sub.identity_review_status === "approved" ? "Level 2" : "Level 1";
                      return (
                        <div
                          key={sub.user_id}
                          className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                            isSelected ? "border-primary bg-primary/5" : "hover:bg-slate-50"
                          }`}
                          onClick={() => setSelectedUser(sub)}
                          data-testid={`kyc-submission-${sub.user_id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
                                <PersonaIcon className="w-5 h-5 text-slate-600" />
                              </div>
                              <div>
                                <p className="font-medium text-sm">{sub.user_name || "Unknown"}</p>
                                <p className="text-xs text-muted-foreground">{sub.user_email}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {tab === "coach" && (
                                <Badge variant="outline" className="text-[9px]">{reviewStage}</Badge>
                              )}
                              <Badge className={statusColors[effectiveStatus] || statusColors.incomplete}>
                                <StatusIcon className="w-3 h-3 mr-1" />
                                {effectiveStatus}
                              </Badge>
                              <ChevronRight className="w-4 h-4 text-muted-foreground" />
                            </div>
                          </div>
                          {sub.gov_id_type && (
                            <p className="text-xs text-muted-foreground mt-2 pl-13">
                              ID: {sub.gov_id_type}{tab === "coach" && sub.licenses?.length > 0 ? ` | ${sub.licenses.length} license(s)` : ""}{tab === "coach" && sub.associations?.length > 0 ? ` | ${sub.associations.length} association(s)` : ""}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Review Panel */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Review Details
                </CardTitle>
              </CardHeader>
              <CardContent>
                {selectedUser ? (
                  <div className="space-y-4">
                    {/* User Info */}
                    <div className="p-4 bg-slate-50 rounded-lg">
                      <h4 className="font-medium mb-2">{selectedUser.user_name}</h4>
                      <p className="text-sm text-muted-foreground">{selectedUser.user_email}</p>
                      <div className="grid grid-cols-2 gap-2 mt-3 text-xs">
                        <div>
                          <span className="text-muted-foreground">Role:</span>
                          <span className="ml-1 font-medium">{selectedUser.venue_role || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">ID Type:</span>
                          <span className="ml-1 font-medium">{selectedUser.gov_id_type || "N/A"}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Status:</span>
                          <Badge className={`ml-1 ${statusColors[selectedUser.identity_review_status] || statusColors.incomplete}`}>
                            {selectedUser.identity_review_status || "incomplete"}
                          </Badge>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Submitted:</span>
                          <span className="ml-1 font-medium">
                            {selectedUser.identity_submitted_at
                              ? new Date(selectedUser.identity_submitted_at).toLocaleDateString()
                              : "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="p-4 border rounded-lg space-y-2 text-sm" data-testid="submitted-profile-details">
                      <h4 className="font-medium">Submitted Profile Details</h4>
                      {tab === "player" && (
                        <>
                          <p><span className="text-muted-foreground">Birthday:</span> {selectedUser.birthday || '—'}</p>
                          <p><span className="text-muted-foreground">Emergency Contact:</span> {selectedUser.emergency_contact || '—'}</p>
                          <p><span className="text-muted-foreground">Facebook:</span> {selectedUser.social_facebook || '—'}</p>
                          <p><span className="text-muted-foreground">Instagram:</span> {selectedUser.social_instagram || '—'}</p>
                        </>
                      )}
                      {tab === "venue_owner" && (
                        <>
                          <p><span className="text-muted-foreground">Legal Entity:</span> {selectedUser.legal_entity_name || '—'}</p>
                          <p><span className="text-muted-foreground">Business Type:</span> {selectedUser.business_type || '—'}</p>
                          <p><span className="text-muted-foreground">Registration #:</span> {selectedUser.business_registration_number || '—'}</p>
                          <p><span className="text-muted-foreground">Venue Address:</span> {selectedUser.venue_physical_address || selectedUser.venue_address || '—'}</p>
                          <p><span className="text-muted-foreground">Business Address:</span> {selectedUser.registered_business_address || '—'}</p>
                          <p><span className="text-muted-foreground">Signatory:</span> {selectedUser.authorized_signatory_name || '—'}</p>
                          <p><span className="text-muted-foreground">Bank Name:</span> {selectedUser.bank_name || '—'}</p>
                          <p><span className="text-muted-foreground">Bank Account Name:</span> {selectedUser.bank_account_name || '—'}</p>
                          <p><span className="text-muted-foreground">Bank Account #:</span> {selectedUser.bank_account_number || '—'}</p>
                          <p><span className="text-muted-foreground">GPS:</span> {selectedUser.gps_latitude && selectedUser.gps_longitude ? `${selectedUser.gps_latitude}, ${selectedUser.gps_longitude}` : '—'}</p>
                        </>
                      )}
                      {tab === "coach" && (
                        <>
                          <p><span className="text-muted-foreground">KYC Level:</span> {selectedUser.kyc_level || 1}</p>
                          <p><span className="text-muted-foreground">Identity Status:</span> {selectedUser.identity_review_status || '—'}</p>
                          <p><span className="text-muted-foreground">Credentials Status:</span> {selectedUser.credentials_review_status || '—'}</p>
                        </>
                      )}
                    </div>

                    {/* Document Previews */}
                    <div className="space-y-3">
                      <h4 className="font-medium text-sm">Uploaded Documents</h4>
                      
                      {/* Government ID */}
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Government ID</span>
                          {resolvedGovIdUrl ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(resolvedGovIdUrl, "_blank")}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          ) : (
                            <Badge variant="outline">Not uploaded</Badge>
                          )}
                        </div>
                        {resolvedGovIdUrl && isPreviewableImage(resolvedGovIdUrl) && (
                          <img
                            src={resolvedGovIdUrl}
                            alt="Government ID"
                            className="mt-2 max-h-40 rounded border object-contain"
                            onError={(e) => (e.target.style.display = "none")}
                          />
                        )}
                      </div>

                      {/* Selfie */}
                      <div className="border rounded-lg p-3">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">Selfie Verification</span>
                          {resolvedSelfieUrl ? (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => window.open(resolvedSelfieUrl, "_blank")}
                            >
                              <Eye className="w-3 h-3 mr-1" />
                              View
                            </Button>
                          ) : (
                            <Badge variant="outline">Not uploaded</Badge>
                          )}
                        </div>
                        {resolvedSelfieUrl && isPreviewableImage(resolvedSelfieUrl) && (
                          <img
                            src={resolvedSelfieUrl}
                            alt="Selfie"
                            className="mt-2 max-h-40 rounded border object-contain"
                            onError={(e) => (e.target.style.display = "none")}
                          />
                        )}
                      </div>

                      {/* Professional Credentials (for Coaches) — Licenses & Associations */}
                      {tab === "coach" && (selectedUser.licenses?.length > 0 || selectedUser.associations?.length > 0) && (
                        <div className="border rounded-lg p-4 bg-purple-50/50 space-y-4" data-testid="sp-credentials-panel">
                          <h5 className="text-sm font-medium flex items-center gap-2">
                            <Award className="w-4 h-4 text-purple-600" />
                            Professional Information (Level 2)
                          </h5>

                          {/* Licenses */}
                          {selectedUser.licenses?.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-heading uppercase text-purple-700 tracking-wider flex items-center gap-1">
                                <FileText className="w-3 h-3" />Licenses ({selectedUser.licenses.length})
                              </p>
                              {selectedUser.licenses.map((lic, idx) => (
                                <div key={idx} className="bg-white border rounded-lg p-3 space-y-1" data-testid={`review-license-${idx}`}>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{lic.name}</span></p>
                                    <p><span className="text-muted-foreground">Issuing Body:</span> <span className="font-medium">{lic.issuing_body}</span></p>
                                    <p><span className="text-muted-foreground">License #:</span> <span className="font-medium">{lic.license_number || '—'}</span></p>
                                    <p><span className="text-muted-foreground">Expiry:</span> <span className="font-medium">{lic.expiry_date || '—'}</span></p>
                                  </div>
                                  {lic.proof_data && (
                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                                      {lic.proof_data.startsWith("data:image") ? (
                                        <img src={lic.proof_data} alt={`License ${idx + 1}`} className="max-h-24 rounded border object-contain cursor-pointer"
                                          onClick={() => { const w = window.open("", "_blank"); w.document.write(`<img src="${lic.proof_data}" style="max-width:100%" />`); }} />
                                      ) : (
                                        <Button size="sm" variant="outline" className="text-xs"
                                          onClick={() => { const w = window.open("", "_blank"); w.document.write(`<iframe src="${lic.proof_data}" width="100%" height="100%" />`); }}>
                                          <Eye className="w-3 h-3 mr-1" />View Proof
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Associations */}
                          {selectedUser.associations?.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-xs font-heading uppercase text-purple-700 tracking-wider flex items-center gap-1">
                                <Globe className="w-3 h-3" />Associations ({selectedUser.associations.length})
                              </p>
                              {selectedUser.associations.map((assoc, idx) => (
                                <div key={idx} className="bg-white border rounded-lg p-3 space-y-1" data-testid={`review-assoc-${idx}`}>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                    <p><span className="text-muted-foreground">Name:</span> <span className="font-medium">{assoc.name}</span></p>
                                    <p><span className="text-muted-foreground">Level:</span> <Badge className="text-[9px] bg-purple-100 text-purple-800">{assoc.membership_level}</Badge></p>
                                    <p><span className="text-muted-foreground">Membership ID:</span> <span className="font-medium">{assoc.membership_id || '—'}</span></p>
                                  </div>
                                  {assoc.proof_data && (
                                    <div className="flex items-center gap-2 mt-2 pt-2 border-t">
                                      {assoc.proof_data.startsWith("data:image") ? (
                                        <img src={assoc.proof_data} alt={`Association ${idx + 1}`} className="max-h-24 rounded border object-contain cursor-pointer"
                                          onClick={() => { const w = window.open("", "_blank"); w.document.write(`<img src="${assoc.proof_data}" style="max-width:100%" />`); }} />
                                      ) : (
                                        <Button size="sm" variant="outline" className="text-xs"
                                          onClick={() => { const w = window.open("", "_blank"); w.document.write(`<iframe src="${assoc.proof_data}" width="100%" height="100%" />`); }}>
                                          <Eye className="w-3 h-3 mr-1" />View Proof
                                        </Button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}

                          {selectedUser.credentials_review_status && (
                            <div className={`p-2 rounded text-xs ${
                              selectedUser.credentials_review_status === "approved" ? "bg-green-100 text-green-800" :
                              selectedUser.credentials_review_status === "rejected" ? "bg-red-100 text-red-800" :
                              "bg-amber-100 text-amber-800"
                            }`}>
                              Credentials: {selectedUser.credentials_review_status}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Review Notes */}
                    {((tab !== "coach" && selectedUser.identity_review_status === "pending") ||
                      (tab === "coach" && (selectedUser.identity_review_status === "pending" || selectedUser.credentials_review_status === "pending"))) && (
                      <div className="space-y-3 pt-4 border-t">
                        {/* Review Type Toggle for Coaches */}
                        {tab === "coach" && selectedUser.identity_review_status === "approved" && selectedUser.credentials_review_status === "pending" && (
                          <div className="flex gap-2 mb-3">
                            <Button
                              variant={reviewType === "identity" ? "default" : "outline"}
                              size="sm"
                              onClick={() => setReviewType("identity")}
                              disabled
                              className="flex-1"
                            >
                              Identity ✓
                            </Button>
                            <Button
                              variant={reviewType === "credentials" ? "default" : "outline"}
                              size="sm"
                              onClick={() => setReviewType("credentials")}
                              className="flex-1"
                            >
                              Credentials
                            </Button>
                          </div>
                        )}
                        {tab === "coach" && selectedUser.identity_review_status === "pending" && (
                          <div className="flex gap-2 mb-3">
                            <Button
                              variant={reviewType === "identity" ? "default" : "outline"}
                              size="sm"
                              onClick={() => setReviewType("identity")}
                              className="flex-1"
                            >
                              Identity
                            </Button>
                            <Button
                              variant={reviewType === "credentials" ? "default" : "outline"}
                              size="sm"
                              onClick={() => setReviewType("credentials")}
                              disabled={selectedUser.identity_review_status !== "approved"}
                              className="flex-1"
                            >
                              Credentials
                            </Button>
                          </div>
                        )}
                        
                        <p className="text-sm text-muted-foreground">
                          Reviewing: <span className="font-medium capitalize">{reviewType}</span>
                        </p>
                        
                        <div>
                          <label className="text-sm font-medium">Review Notes (optional)</label>
                          <Textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Add notes for the user (e.g., reason for rejection)..."
                            className="mt-1"
                            rows={3}
                          />
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            className="flex-1 bg-green-600 hover:bg-green-700"
                            onClick={() => handleReview("approve")}
                            disabled={processing}
                          >
                            {processing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Approve {tab === "coach" && reviewType === "credentials" ? "Credentials" : "Identity"}
                              </>
                            )}
                          </Button>
                          <Button
                            className="flex-1"
                            variant="outline"
                            onClick={() => handleReview("request_more_info")}
                            disabled={processing}
                          >
                            {processing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <AlertTriangle className="w-4 h-4 mr-2" />
                                More Info
                              </>
                            )}
                          </Button>
                          <Button
                            className="flex-1"
                            variant="destructive"
                            onClick={() => handleReview("reject")}
                            disabled={processing}
                          >
                            {processing ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <>
                                <XCircle className="w-4 h-4 mr-2" />
                                Reject
                              </>
                            )}
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Previous Review Notes */}
                    {selectedUser.identity_review_notes && (
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs font-medium text-muted-foreground mb-1">Identity Review Notes:</p>
                        <p className="text-sm">{selectedUser.identity_review_notes}</p>
                        {selectedUser.identity_reviewed_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Reviewed: {new Date(selectedUser.identity_reviewed_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    {selectedUser.credentials_review_notes && (
                      <div className="p-3 bg-purple-50 rounded-lg">
                        <p className="text-xs font-medium text-purple-700 mb-1">Credentials Review Notes:</p>
                        <p className="text-sm">{selectedUser.credentials_review_notes}</p>
                        {selectedUser.credentials_reviewed_at && (
                          <p className="text-xs text-muted-foreground mt-2">
                            Reviewed: {new Date(selectedUser.credentials_reviewed_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <User className="w-12 h-12 mx-auto mb-2 opacity-20" />
                    <p>Select a submission to review</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </Tabs>
    </div>
  );
}
